(ns dashboard.views.browser-extension.questionnaire.questions.view
  (:require ["react" :as react]
            [clojure.string :as str]
            [dashboard.browser-extension :as browser-extension]
            [dashboard.entity.analysis.db :as analysis.db]
            [dashboard.entity.analysis.modals :as analysis.view]
            [dashboard.entity.analysis.policy-process-pairs :as process<->policy]
            [dashboard.entity.analysis.search :as analysis.search]
            [dashboard.entity.question.analysis-search :as question.analysis.search]
            [dashboard.entity.question.answer-scope :as answer-scope]
            [dashboard.entity.question.comparison :as comp.compare]
            [dashboard.entity.question.db :as questions.db]
            [dashboard.entity.question.profile :as question.profile]
            [dashboard.entity.questionnaire.db :as questionnaire.db]
            [dashboard.services.answers :as answers]
            [dashboard.services.document-list :as documents]
            [dashboard.services.question-note :as question-note]
            [dashboard.services.questionnaire-list :as questionnaire-list]
            [dashboard.subs :as common-subs]
            [dashboard.ui :as ui]
            [dashboard.views.questionnaire.components.archived-list :as archives]
            [dashboard.views.questionnaire.questions.view :as questions.view]
            [re-frame.core :as rf]))

(def controllers
  [{:parameters {:path [:questionnaire-id]}
    :start (fn [{{:keys [questionnaire-id]} :path}]
             (let [questionnaire-id (uuid (str questionnaire-id))]
               (analysis.db/get-applied-analyses false)
               (analysis.db/get-categories false)
               (analysis.db/get-structured-analyses false)
               (analysis.db/get-structured-analysis-questions false)
               (questionnaire.db/get-attachments questionnaire-id)
               (questionnaire.db/get-metadata questionnaire-id nil)
               (questionnaire.db/get-questions questionnaire-id nil)
               (questions.db/get-sections questionnaire-id)
               (questions.db/init-comparisons questionnaire-id)
               (questions.db/init-view questionnaire-id)
               (rf/dispatch [::answer-scope/get])
               (rf/dispatch [::archives/fetch-archived-list questionnaire-id])
               (rf/dispatch [::documents/get])
               (rf/dispatch [::question-note/get-notes questionnaire-id])
               (rf/dispatch [::question-note/get-notes-last-seen questionnaire-id])
               (rf/dispatch [::question-note/start-polling-for-notes questionnaire-id])
               (rf/dispatch [::questionnaire-list/get])))
    :stop (fn [_] (rf/dispatch [::question-note/stop-polling-for-notes]))}])

(def view-id :route.view/extension-question-view)
(def list-id :route.view/extension-question-list)

(defn load-controller
  [questionnaire-id]
  ((-> controllers first :start)
   {:path {:questionnaire-id questionnaire-id}}))

(defn question-row [questionnaire-id input-question-id on-scroll-to]
  (let [features-enabled @(rf/subscribe [::common-subs/features-enabled])
        [ref set-ref] (react/useState nil)

        questions @(questions.db/questions* questionnaire-id)
        questions (vec questions)

        construct-id->categories @(analysis.db/construct-id->categories*)
        process-policy-pairs @(process<->policy/get-policy-process-pairs*)

        all-analysis @(analysis.search/all-analysis-structures)

        on-link-analysis (fn [callback questionnaire-id question-id analyses]
                           (analysis.db/link-analysis
                            (fn [& args]
                              (when (fn? callback) (apply callback args))
                              (rf/dispatch [::ui/pop-modal]))
                            questionnaire-id
                            question-id
                            analyses))
        on-commit-edit (fn [question field-key value]
                         (when (str/blank? (:status question))
                           (questionnaire.db/update-question-status
                            questionnaire-id
                            (:id question)
                            "approved"))
                         (questions.db/commit-edit question field-key value))

        on-update-answer (fn [question coll answer-data]
                           (let [existing-response (-> question
                                                       :selected-answer
                                                       :response)
                                 existing-additional-information (-> question
                                                                     :selected-answer
                                                                     :additional-information)
                                 answer-data (assoc answer-data
                                                    :response
                                                    (str existing-response
                                                         (if (str/blank? existing-response) "" "\n\n")
                                                         (:response answer-data))
                                                    :additional-information
                                                    (str existing-additional-information
                                                         (if (str/blank? existing-additional-information) "" "\n\n")
                                                         (:additional-information answer-data)))]
                             (questions.db/commit-update
                              (assoc question :selected-answer
                                     (-> question
                                         :selected-answer
                                         (merge answer-data)
                                         (assoc :applied-analyses (vec coll)))))
                             (when (str/blank? (:status question))
                               (questionnaire.db/update-question-status
                                questionnaire-id
                                (:id question)
                                "approved"))
                             (analysis.search/set-selected-question
                              [:analysis-search :questions (:id question) :recommended-question]
                              nil)
                             (analysis.search/set-selected-question
                              [:analysis-search :questions (:id question) :recommended-analysis]
                              nil)
                             (analysis.search/set-selected-analysis
                              [:analysis-search :questions (:id question) :recommended-analysis]
                              nil)))
        on-unlink-analysis (fn [questionnaire-id
                                question-id
                                answer-id
                                construct-id
                                version-id
                                callback]
                             (analysis.db/unlink-applied-analysis
                              questionnaire-id
                              question-id
                              answer-id
                              construct-id
                              version-id
                              callback))
        selected-question (->> questions
                               (filter (fn [q] (= (:id q) input-question-id)))
                               first)]
    (react/useEffect
     (fn [_]
       (questions.db/set-filters questionnaire-id {"section" nil}))
     #js [nil])

    (react/useEffect
     (fn [_]
       (when ref
         (.scrollIntoView ref #js{:behavior "smooth"
                                  :block "center"})))
     #js [ref])

    (react/useEffect
     (fn [_]
       (rf/dispatch [::browser-extension/send-event
                     {:type :update-selected-question-data
                      :data selected-question}]))
     #js [selected-question])

    [question.profile/question-wrapper
     [:div.section.pt-0.pb-3.questions--big-bottom
      {:ref set-ref}
      (let [q selected-question]
        [:div.questions-question-list--item
         [:div.is-flex-grow-1.px-4
          [:f> question.profile/Question
           {:features-enabled features-enabled
            :answer-actions-ui (partial questions.view/AnswerActions (partial on-update-answer q))
            :analysis-actions-ui questions.view/AnalysisActions
            :analysis-search-ui question.analysis.search/search-analysis-by-category-modal
            :question-lab-ui (constantly nil)
            :comparison-symbol-ui comp.compare/ComparisonSymbol
            :construct->categories (comp #(get construct-id->categories %) :construct-id)
            :process-policy-pairs process-policy-pairs
            :on-scroll-to on-scroll-to
            :on-update-scope #(rf/dispatch [::answers/set-answer-scope
                                            questionnaire-id
                                            #{(:id q)}
                                            %])
            :on-update-status #(questionnaire.db/update-question-status
                                questionnaire-id
                                (:id q)
                                %)
            :on-analysis-action (fn [action analysis menu-ref set-modal-instance too-many-args]
                                  (questions.view/LinkedAnalysisActions
                                   {:question q
                                    :all-analysis all-analysis
                                    :on-update-answer (partial on-update-answer q)
                                    :on-link-analysis (partial on-link-analysis
                                                               identity
                                                               questionnaire-id
                                                               (:id q))
                                    :on-unlink-analysis (partial on-unlink-analysis
                                                                 questionnaire-id
                                                                 (:id q)
                                                                 (-> q :selected-answer :id))
                                    :policy-process-pairs process-policy-pairs
                                    :action action
                                    :analysis analysis
                                    :menu-ref menu-ref
                                    :set-modal-instance set-modal-instance}))
            :on-commit-edit on-commit-edit
            :on-click-construct #(on-scroll-to)
            :on-click-state (fn [cid asa]
                              (analysis.view/show-editor-modal
                               (if (:next-version-id asa) :view :edit)
                               cid
                               asa))
            :on-link-analysis on-link-analysis
            :on-unlink-analysis on-unlink-analysis
            :on-report-analysis-suggestion (fn [construct]
                                             (questions.db/report-analysis-suggestion
                                              (:content q)
                                              construct
                                              false))}
           q]]])]]))